import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import Layout from "../components/Layout";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
import { Upload, ArrowRight, FileSpreadsheet, Sparkles, ShieldCheck, X, Download } from "lucide-react";
import { toast } from "sonner";

export default function UploadWizard() {
  const { pid } = useParams();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [file, setFile] = useState(null);
  const [parsed, setParsed] = useState(null);
  const [mapping, setMapping] = useState({ indicator_column: "", period_column: "", value_column: "" });
  const [axisColumns, setAxisColumns] = useState({});
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [indicators, setIndicators] = useState([]);
  const [analysis, setAnalysis] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [accepting, setAccepting] = useState(false);
  const [acceptedKeys, setAcceptedKeys] = useState({}); // column_name -> true
  const [acceptedFixes, setAcceptedFixes] = useState({}); // fix_id -> true
  const [applyingFixes, setApplyingFixes] = useState(false);
  // Phase 3.26 — Option B spec: "Show on Dashboard" checkboxes per indicator.
  // Selections are merged into the user's per-browser pinned-indicator list
  // (localStorage key `ae-dashboard-pinned-v1`) when "Run validation" fires.
  const [pinSelections, setPinSelections] = useState({}); // indicator_id -> true

  // Aggregate all required axes across the program's indicators.
  const requiredAxes = (() => {
    const out = {};
    for (const i of indicators) {
      for (const a of (i.disaggregation_axes || [])) {
        if (!a?.key) continue;
        if (!out[a.key]) out[a.key] = { key: a.key, values: a.values || [], required: !!a.required, indicators: [] };
        out[a.key].indicators.push(i.code);
        if (a.required) out[a.key].required = true;
      }
    }
    return Object.values(out);
  })();

  useEffect(() => {
    api.get(`/programs/${pid}/indicators`).then((r) => setIndicators(r.data));
  }, [pid]);

  const onUpload = async (e) => {
    e.preventDefault();
    if (!file) return;
    setError(""); setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/programs/${pid}/uploads/parse`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setParsed(data);
      const cols = data.columns;
      const guess = (kws) => cols.find((c) => kws.some((k) => c.toLowerCase().includes(k))) || "";
      setMapping({
        indicator_column: guess(["indicator", "code", "metric"]),
        period_column: guess(["period", "month", "quarter", "year", "date"]),
        value_column: guess(["value", "result", "amount", "count"]),
      });
      // Auto-guess axis columns by case-insensitive header match
      const auto = {};
      for (const a of requiredAxes) {
        const found = data.columns.find((c) => c.toLowerCase().includes(a.key.toLowerCase()));
        if (found) auto[a.key] = found;
      }
      setAxisColumns(auto);
      setStep(2);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  const onMap = async () => {
    setError(""); setBusy(true);
    try {
      const { data } = await api.post(`/uploads/${parsed.id}/map`, {
        indicator_column: mapping.indicator_column,
        period_column: mapping.period_column,
        value_column: mapping.value_column,
        disaggregation_columns: [],
        axis_columns: axisColumns,
      });
      // Merge "Show on Dashboard" picks into the user's pinned-indicator
      // list. Keep up to 4 (the dashboard's Row 2 capacity); newer picks
      // win when the cap is exceeded.
      const newlyPinned = Object.keys(pinSelections).filter((k) => pinSelections[k]);
      if (newlyPinned.length) {
        try {
          const raw = localStorage.getItem("ae-dashboard-pinned-v1");
          const existing = raw ? JSON.parse(raw) : [];
          const merged = [...newlyPinned, ...existing.filter((id) => !newlyPinned.includes(id))].slice(0, 4);
          localStorage.setItem("ae-dashboard-pinned-v1", JSON.stringify(merged));
        } catch { /* ignore storage failure */ }
      }
      navigate(`/programs/${pid}/uploads/${parsed.id}/validate?summary=${data.clean}-${data.dirty}&superseded=${data.superseded || 0}`);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  const runSmartSuggest = async () => {
    if (!parsed) return;
    setAnalyzing(true);
    try {
      const { data } = await api.post(`/uploads/${parsed.id}/analyze`);
      setAnalysis(data);
      // Pre-tick all candidates by default
      const ticked = {};
      for (const c of data.candidates) ticked[c.column_name] = true;
      setAcceptedKeys(ticked);
      // Pre-tick all HIGH-confidence (>=0.8) fixes by default; lower-confidence
      // fixes (e.g. range midpoints) are left unchecked so the user opts in.
      const tickedFixes = {};
      for (const f of (data.auto_fixes || [])) {
        if ((f.confidence ?? 0) >= 0.8) tickedFixes[f.id] = true;
      }
      setAcceptedFixes(tickedFixes);
      const fixCount = (data.auto_fixes || []).length;
      const candCount = data.candidates.length;
      if (candCount === 0 && fixCount === 0) {
        toast.info("No clean numeric metrics or fixable rows detected.");
      } else {
        const parts = [];
        if (candCount) parts.push(`${candCount} candidate indicator${candCount === 1 ? "" : "s"}`);
        if (fixCount) parts.push(`${fixCount} auto-fix${fixCount === 1 ? "" : "es"}`);
        toast.success(`Detected ${parts.join(" + ")}.`);
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Smart-suggest failed");
    } finally { setAnalyzing(false); }
  };

  const applyAutoFixes = async () => {
    if (!analysis) return;
    const ids = Object.keys(acceptedFixes).filter((k) => acceptedFixes[k]);
    if (ids.length === 0) {
      toast.error("Select at least one fix to apply.");
      return;
    }
    setApplyingFixes(true);
    try {
      const { data } = await api.post(`/uploads/${parsed.id}/apply-auto-fixes`, { fix_ids: ids });
      const dropped = data.rows_dropped || 0;
      const rewritten = data.cells_rewritten || 0;
      toast.success(
        `Applied ${data.applied} fix${data.applied === 1 ? "" : "es"} · ${rewritten} cell${rewritten === 1 ? "" : "s"} rewritten` +
        (dropped ? ` · ${dropped} row${dropped === 1 ? "" : "s"} dropped` : "")
      );
      // Re-run analysis so the panel reflects the cleaned data
      const refreshed = await api.post(`/uploads/${parsed.id}/analyze`);
      setAnalysis(refreshed.data);
      // Re-tick remaining high-confidence fixes; keep prior candidate ticks.
      const tickedFixes = {};
      for (const f of (refreshed.data.auto_fixes || [])) {
        if ((f.confidence ?? 0) >= 0.8) tickedFixes[f.id] = true;
      }
      setAcceptedFixes(tickedFixes);
      // Refresh parsed.row_count + preview
      setParsed({ ...parsed, row_count: data.row_count });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not apply fixes");
    } finally { setApplyingFixes(false); }
  };

  const acceptSuggested = async () => {
    if (!analysis) return;
    const picked = (analysis.candidates || []).filter((c) => acceptedKeys[c.column_name]);
    if (picked.length === 0) {
      toast.error("Select at least one candidate to accept.");
      return;
    }
    setAccepting(true);
    try {
      const { data } = await api.post(`/uploads/${parsed.id}/accept-candidates`, {
        candidates: picked,
        period_column: analysis.summary.period_columns[0] || null,
      });
      const m = data.applied_mapping;
      setMapping({
        indicator_column: m.indicator_column,
        period_column: m.period_column,
        value_column: m.value_column,
      });
      // Refresh indicator list so the program sidebar shows the new ones
      const inds = await api.get(`/programs/${pid}/indicators`);
      setIndicators(inds.data);
      toast.success(`Created ${data.created_indicator_ids.length} indicator${data.created_indicator_ids.length === 1 ? "" : "s"} · mapping pre-filled. Click Run validation.`);
      // Re-fetch parsed with the new synthetic 'indicator_code' column if added
      if (m.indicator_column === "indicator_code" && !parsed.columns.includes("indicator_code")) {
        setParsed({ ...parsed, columns: [...parsed.columns, "indicator_code"] });
      }
      setAnalysis(null);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not accept candidates");
    } finally { setAccepting(false); }
  };

  // Triggers a client-side CSV download of the canonical Array Engine
  // template — Date, Indicator_Name, Value, Location, Theme — plus two
  // example rows so first-time uploaders can see the shape they need.
  const downloadTemplate = () => {
    const headers = ["Date", "Indicator_Name", "Value", "Location", "Theme"];
    const sample = [
      ["2026-01-15", "Children enrolled", "1500", "Lagos", "Education"],
      ["2026-01-15", "Teachers trained", "62", "Kano", "Education"],
    ];
    const lines = [headers.join(","), ...sample.map((r) => r.join(","))];
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "array-engine-upload-template.csv";
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 2000);
    toast.success("Template downloaded — fill it in then upload.");
  };

  return (
    <Layout>
      <Link to={`/programs/${pid}`} className="label-overline hover:text-slate-900" data-testid="back-program-link">← Back to program</Link>
      <h1 className="font-heading text-4xl font-semibold mt-3">Data ingestion</h1>
      <p className="text-slate-600 mt-2">Upload a CSV or Excel file, then map its columns to your indicators.</p>

      <div className="mt-8 grid grid-cols-3 gap-2 max-w-xl">
        {["Upload", "Map columns", "Validate"].map((s, i) => (
          <div key={s} className={`border-t-2 pt-3 ${step >= i+1 ? "border-slate-900" : "border-slate-200"}`}>
            <div className={`label-overline ${step >= i+1 ? "text-slate-900" : "text-slate-400"}`}>Step {i+1}</div>
            <div className={`text-sm mt-1 ${step >= i+1 ? "text-slate-900 font-medium" : "text-slate-500"}`}>{s}</div>
          </div>
        ))}
      </div>

      {step === 1 && (
        <form onSubmit={onUpload} className="mt-10 max-w-xl bg-white border border-slate-200 p-8 rounded-sm" data-testid="upload-step-form">
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="label-overline">Source file</p>
              <h3 className="font-heading text-2xl mt-2">Upload CSV or Excel</h3>
              <p className="text-sm text-slate-600 mt-1">First row should contain column headers. Supported: .csv, .xlsx, .xls</p>
            </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={downloadTemplate}
              className="shrink-0"
              data-testid="download-template-btn"
              title="Download a CSV template with the standard headers"
            >
              <Download className="w-3.5 h-3.5 mr-1.5" /> Download template
            </Button>
          </div>
          <p className="text-[11px] text-slate-500 mt-3 bg-slate-50 border border-slate-100 rounded-sm p-2.5 leading-relaxed">
            Not sure how to format your data? Download the template above — it includes the standard
            <span className="font-mono mx-1">Date, Indicator_Name, Value, Location, Theme</span>
            headers with two example rows.
          </p>
          <label className="mt-6 block border-2 border-dashed border-slate-300 hover:border-slate-900 transition-colors cursor-pointer p-10 text-center rounded-sm">
            <Upload className="w-8 h-8 mx-auto text-slate-400" />
            <div className="mt-3 font-medium">{file ? file.name : "Click to select a file"}</div>
            <div className="text-xs text-slate-500 mt-1">CSV / XLSX up to a few MB</div>
            <input type="file" accept=".csv,.xlsx,.xls" className="hidden" onChange={(e)=>setFile(e.target.files[0])} data-testid="upload-file-input" />
          </label>
          {error && <div className="mt-4 text-red-700 bg-red-50 border border-red-200 p-3 text-sm">{error}</div>}
          <div className="mt-6 flex justify-end">
            <Button type="submit" disabled={!file || busy} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="upload-submit-button">
              {busy ? "Parsing…" : "Continue"} <ArrowRight className="w-4 h-4 ml-1"/>
            </Button>
          </div>
        </form>
      )}

      {step === 2 && parsed && (
        <div className="mt-10 grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 bg-white border border-slate-200 rounded-sm overflow-hidden" data-testid="preview-table">
            <div className="px-4 py-3 border-b border-slate-200 flex items-center gap-2">
              <FileSpreadsheet className="w-4 h-4 text-slate-500"/>
              <span className="font-mono text-xs">{parsed.filename}</span>
              <span className="ml-auto label-overline">Preview · {parsed.row_count} rows total</span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead><tr className="bg-slate-100">
                  {parsed.columns.map((c) => <th key={c} className="text-left px-3 py-2 font-medium border-r border-slate-200 last:border-r-0">{c}</th>)}
                </tr></thead>
                <tbody>
                  {parsed.preview.map((row, i) => (
                    <tr key={i} className="border-t border-slate-100">
                      {parsed.columns.map((c) => <td key={c} className="px-3 py-2 border-r border-slate-100 last:border-r-0">{row[c]}</td>)}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          <div className="bg-white border border-slate-200 p-6 rounded-sm" data-testid="mapping-form">
            <div className="flex items-center justify-between gap-2">
              <div>
                <p className="label-overline">Step 2</p>
                <h3 className="font-heading text-xl mt-1">Map columns to indicators</h3>
              </div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={runSmartSuggest}
                disabled={analyzing}
                data-testid="smart-suggest-btn"
                title="Auto-detect column types + indicator candidates from your data"
              >
                <Sparkles className="w-3.5 h-3.5 mr-1" /> {analyzing ? "Analysing…" : "Smart suggest"}
              </Button>
            </div>
            <p className="text-xs text-slate-500 mt-2">
              We use the <strong>indicator code</strong> (or name) in your file to match it to indicators in this program.
            </p>

            {analysis && (
              <SmartSuggestPanel
                analysis={analysis}
                accepted={acceptedKeys}
                setAccepted={setAcceptedKeys}
                onAccept={acceptSuggested}
                onClose={() => setAnalysis(null)}
                accepting={accepting}
                acceptedFixes={acceptedFixes}
                setAcceptedFixes={setAcceptedFixes}
                onApplyFixes={applyAutoFixes}
                applyingFixes={applyingFixes}
              />
            )}
            <div className="mt-5 space-y-4">
              <FieldSelect label="Indicator column" value={mapping.indicator_column} onChange={(v)=>setMapping({...mapping, indicator_column: v})} options={parsed.columns} testid="map-indicator-select" />
              <FieldSelect label="Period column" value={mapping.period_column} onChange={(v)=>setMapping({...mapping, period_column: v})} options={parsed.columns} testid="map-period-select" />
              <FieldSelect label="Value column" value={mapping.value_column} onChange={(v)=>setMapping({...mapping, value_column: v})} options={parsed.columns} testid="map-value-select" />
            </div>

            {/* Phase 2 — BI dimensions. All optional; left null when not mapped. */}
            <div className="mt-6 border-t border-slate-100 pt-4" data-testid="bi-dims-mapping-section">
              <p className="label-overline mb-1">BI dimensions <span className="text-slate-400 normal-case tracking-normal">(optional)</span></p>
              <p className="text-xs text-slate-500 mb-4">
                Map columns to first-class BI dimensions. Each appears as a filter in the Live Dashboard
                and is mirrored into the row&apos;s disaggregation map so existing axis discovery still works.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <FieldSelect
                  label="Funding source column" allowEmpty emptyLabel="— none —"
                  value={mapping.funding_source_column || ""}
                  onChange={(v) => setMapping({...mapping, funding_source_column: v || null})}
                  options={parsed.columns}
                  testid="map-funding-select"
                />
                <FieldSelect
                  label="Implementing partner column" allowEmpty emptyLabel="— none —"
                  value={mapping.implementing_partner_column || ""}
                  onChange={(v) => setMapping({...mapping, implementing_partner_column: v || null})}
                  options={parsed.columns}
                  testid="map-partner-select"
                />
                <FieldSelect
                  label="Beneficiary group column" allowEmpty emptyLabel="— none —"
                  value={mapping.beneficiary_group_column || ""}
                  onChange={(v) => setMapping({...mapping, beneficiary_group_column: v || null})}
                  options={parsed.columns}
                  testid="map-beneficiary-select"
                />
              </div>
            </div>
            {requiredAxes.length > 0 && (
              <div className="mt-6 border-t border-slate-100 pt-4" data-testid="axis-mapping-section">
                <p className="label-overline mb-1">Disaggregation axes</p>
                <p className="text-xs text-slate-500 mb-4">
                  Some indicators in this program declare required disaggregation axes. Pick the CSV column that carries each axis. <span className="text-amber-700">Required</span> axes will dirty-flag rows missing or off-vocabulary values.
                </p>
                <div className="space-y-3">
                  {requiredAxes.map((a) => (
                    <div key={a.key}>
                      <Label className="label-overline flex items-center gap-2">
                        <span>{a.key} column</span>
                        {a.required && <span className="text-[10px] text-amber-700 normal-case tracking-normal">· required</span>}
                        {a.values?.length > 0 && <span className="text-[10px] text-slate-400 normal-case tracking-normal">· allowed: {a.values.join(", ")}</span>}
                      </Label>
                      <Select value={axisColumns[a.key] || "__none__"} onValueChange={(v)=>{
                        const next = {...axisColumns};
                        if (v === "__none__") delete next[a.key]; else next[a.key] = v;
                        setAxisColumns(next);
                      }}>
                        <SelectTrigger className="mt-2" data-testid={`map-axis-${a.key}-select`}>
                          <SelectValue placeholder="Skip — no column for this axis" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__none__">Skip — no column for this axis</SelectItem>
                          {parsed.columns.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <div className="mt-6 border-t border-slate-100 pt-4" data-testid="show-on-dashboard-section">
              <p className="label-overline mb-1">Show on Dashboard <span className="text-slate-400 normal-case tracking-normal">(up to 4)</span></p>
              <p className="text-xs text-slate-500 mb-3">
                Tick any indicators from this program to pin them to your dashboard&apos;s 8-KPI Row 2.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5 max-h-44 overflow-y-auto pr-1" data-testid="show-on-dashboard-list">
                {indicators.length === 0 && (
                  <p className="text-xs text-slate-400 col-span-full">No indicators in this program yet.</p>
                )}
                {indicators.map((i) => (
                  <label
                    key={i.id}
                    className="flex items-start gap-2 text-xs cursor-pointer hover:bg-slate-50 px-2 py-1 rounded-sm"
                    data-testid={`pin-${i.code || i.id}`}
                  >
                    <input
                      type="checkbox"
                      className="mt-0.5 accent-emerald-600"
                      checked={!!pinSelections[i.id]}
                      onChange={(e) => setPinSelections({ ...pinSelections, [i.id]: e.target.checked })}
                    />
                    <span className="font-mono text-[11px] text-slate-700 shrink-0">{i.code}</span>
                    <span className="text-slate-500 truncate">{i.name}</span>
                  </label>
                ))}
              </div>
            </div>
            <div className="mt-6 border-t border-slate-100 pt-4">
              <p className="label-overline mb-2">Indicators in this program ({indicators.length})</p>
              <div className="flex flex-wrap gap-1">
                {indicators.map((i) => <span key={i.id} className="text-xs font-mono bg-slate-100 px-2 py-1 rounded">{i.code}</span>)}
              </div>
            </div>
            {error && <div className="mt-4 text-red-700 bg-red-50 border border-red-200 p-3 text-sm">{error}</div>}
            <Button onClick={onMap} disabled={busy || !mapping.indicator_column || !mapping.period_column || !mapping.value_column} className="w-full mt-6 bg-slate-900 hover:bg-slate-800 text-white" data-testid="apply-mapping-button">
              {busy ? "Validating…" : "Run validation"} <ArrowRight className="w-4 h-4 ml-1"/>
            </Button>
          </div>
        </div>
      )}
    </Layout>
  );
}

function SmartSuggestPanel({ analysis, accepted, setAccepted, onAccept, onClose, accepting, acceptedFixes, setAcceptedFixes, onApplyFixes, applyingFixes }) {
  const cols = analysis.columns || [];
  const candidates = analysis.candidates || [];
  const fixes = analysis.auto_fixes || [];
  const fixesByKind = fixes.reduce((acc, f) => {
    (acc[f.kind] = acc[f.kind] || []).push(f);
    return acc;
  }, {});
  const kindLabels = {
    approx_prefix: "Approximation language",
    trailing_unit: "Number with trailing unit",
    range_midpoint: "Range → midpoint",
    nullish_blank: "Placeholder for missing",
    total_row_drop: "Subtotal / total row",
    period_normalize: "Period format",
  };
  const checkedFixCount = Object.values(acceptedFixes || {}).filter(Boolean).length;
  const toggleAllFixes = (on) => {
    const next = {};
    if (on) for (const f of fixes) next[f.id] = true;
    setAcceptedFixes(next);
  };
  const typeBadge = (t) => {
    const map = {
      NUMERIC_METRIC: "bg-emerald-50 text-emerald-800 border-emerald-200",
      PERCENTAGE: "bg-emerald-50 text-emerald-800 border-emerald-200",
      CURRENCY: "bg-emerald-50 text-emerald-800 border-emerald-200",
      DATE: "bg-sky-50 text-sky-800 border-sky-200",
      CATEGORY: "bg-violet-50 text-violet-800 border-violet-200",
      IDENTIFIER: "bg-slate-100 text-slate-700 border-slate-200",
      BOOLEAN: "bg-amber-50 text-amber-800 border-amber-200",
      TEXT: "bg-slate-50 text-slate-600 border-slate-200",
      EMPTY: "bg-slate-50 text-slate-400 border-slate-200",
    };
    return map[t] || "bg-slate-50 text-slate-600 border-slate-200";
  };

  return (
    <div
      className="mt-4 mb-4 bg-slate-900 text-white rounded-sm overflow-hidden"
      data-testid="smart-suggest-panel"
    >
      <div className="px-4 py-3 flex items-center justify-between border-b border-slate-700">
        <div className="flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-amber-300" />
          <p className="label-overline text-amber-200">Smart suggestions</p>
        </div>
        <button onClick={onClose} className="text-slate-400 hover:text-white" data-testid="smart-suggest-close">
          <X className="w-4 h-4" />
        </button>
      </div>

      <div className="p-4 space-y-4 text-sm">
        <section>
          <p className="label-overline text-slate-400">Inferred column types</p>
          <div className="mt-2 flex flex-wrap gap-1.5">
            {cols.map((c) => (
              <span
                key={c.column_name}
                title={c.reasoning}
                className={`text-[11px] inline-flex items-center gap-1 px-2 py-0.5 rounded-sm border ${typeBadge(c.inferred_type)}`}
                data-testid={`column-type-${c.column_name}`}
              >
                <span className="font-mono">{c.column_name}</span>
                <span className="opacity-60">·</span>
                <span>{c.inferred_type}</span>
                <span className="opacity-60">{Math.round((c.confidence_score || 0) * 100)}%</span>
              </span>
            ))}
          </div>
        </section>

        {fixes.length > 0 && (
          <section data-testid="auto-fix-section">
            <div className="flex items-center justify-between">
              <p className="label-overline text-slate-400">
                Auto-fix dirty rows · {fixes.length} suggestion{fixes.length === 1 ? "" : "s"}
              </p>
              <div className="flex gap-3 text-[11px] text-amber-200">
                <button
                  type="button"
                  onClick={() => toggleAllFixes(true)}
                  className="hover:text-white"
                  data-testid="fix-select-all"
                >Select all</button>
                <button
                  type="button"
                  onClick={() => toggleAllFixes(false)}
                  className="hover:text-white"
                  data-testid="fix-clear-all"
                >Clear</button>
              </div>
            </div>
            <p className="text-[11px] text-slate-400 mt-1">
              Confidence ≥ 80% pre-checked. Review lower-confidence fixes (e.g. ranges) before applying.
            </p>
            <div className="mt-2 space-y-3 max-h-72 overflow-y-auto pr-1">
              {Object.entries(fixesByKind).map(([kind, list]) => (
                <div key={kind} data-testid={`fix-group-${kind}`}>
                  <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">
                    {kindLabels[kind] || kind} · {list.length}
                  </p>
                  <ul className="space-y-1.5">
                    {list.map((f) => {
                      const checked = !!(acceptedFixes && acceptedFixes[f.id]);
                      return (
                        <li
                          key={f.id}
                          onClick={() => setAcceptedFixes({ ...acceptedFixes, [f.id]: !checked })}
                          className={`border rounded-sm px-2.5 py-1.5 cursor-pointer text-[11px] flex items-start gap-2 ${
                            checked ? "bg-slate-800 border-amber-400" : "bg-slate-800/40 border-slate-700"
                          }`}
                          data-testid={`fix-item-${f.id}`}
                          title={f.reasoning}
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={(e) => { e.stopPropagation(); setAcceptedFixes({ ...acceptedFixes, [f.id]: e.target.checked }); }}
                            className="mt-0.5 accent-amber-400"
                            data-testid={`fix-check-${f.id}`}
                          />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              {f.column && (
                                <span className="font-mono text-slate-300">{f.column}</span>
                              )}
                              <span className="text-slate-400">row {f.row_index + 1}</span>
                              <span className="text-slate-500">·</span>
                              <span className="text-slate-300">{f.issue}</span>
                              <span className="ml-auto text-slate-500">{Math.round((f.confidence || 0) * 100)}%</span>
                            </div>
                            <div className="mt-0.5 font-mono text-slate-200 break-all">
                              <span className="line-through text-rose-300/80">{f.original}</span>
                              {f.suggested !== null && f.suggested !== undefined && (
                                <>
                                  <span className="text-slate-500 mx-1">→</span>
                                  <span className="text-emerald-300">
                                    {f.suggested === "" ? "(blank)" : f.suggested}
                                  </span>
                                </>
                              )}
                              {(f.suggested === null || f.suggested === undefined) && (
                                <>
                                  <span className="text-slate-500 mx-1">→</span>
                                  <span className="text-rose-300">drop row</span>
                                </>
                              )}
                            </div>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              ))}
            </div>
            <div className="flex items-center justify-between pt-2 border-t border-slate-700 mt-3">
              <p className="text-[11px] text-slate-400">
                Applying rewrites the upload&apos;s raw rows. Mapping &amp; validation run afterwards.
              </p>
              <Button
                onClick={onApplyFixes}
                disabled={applyingFixes || checkedFixCount === 0}
                className="bg-emerald-400 hover:bg-emerald-300 text-slate-900"
                data-testid="apply-fixes-btn"
              >
                <ShieldCheck className="w-3.5 h-3.5 mr-1" /> {applyingFixes ? "Applying…" : `Apply ${checkedFixCount || ""} fix${checkedFixCount === 1 ? "" : "es"}`.trim()}
              </Button>
            </div>
          </section>
        )}

        <section>
          <div className="flex items-center justify-between">
            <p className="label-overline text-slate-400">Candidate indicators · top {candidates.length}</p>
          </div>
          {candidates.length === 0 ? (
            <p className="text-xs text-slate-400 mt-2">No clean numeric metrics detected — map manually below.</p>
          ) : (
            <ul className="mt-2 space-y-2">
              {candidates.map((c) => {
                const ind = c.suggested_indicator;
                const checked = !!accepted[c.column_name];
                return (
                  <li
                    key={c.column_name}
                    className={`border rounded-sm p-3 cursor-pointer transition ${
                      checked ? "bg-slate-800 border-amber-400" : "bg-slate-800/40 border-slate-700"
                    }`}
                    onClick={() => setAccepted({ ...accepted, [c.column_name]: !checked })}
                    data-testid={`candidate-${c.column_name}`}
                  >
                    <div className="flex items-start gap-3">
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={(e) => { e.stopPropagation(); setAccepted({ ...accepted, [c.column_name]: e.target.checked }); }}
                        className="mt-1 accent-amber-400"
                        data-testid={`candidate-check-${c.column_name}`}
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <h4 className="font-medium text-white">{ind.name}</h4>
                          {c.ai_polished && (
                            <span className="text-[10px] px-1.5 py-0.5 rounded-sm bg-amber-400 text-slate-900 font-medium uppercase tracking-wider">
                              AI
                            </span>
                          )}
                          <span className="text-[10px] text-slate-400 font-mono">{ind.code}</span>
                        </div>
                        <p className="text-[12px] text-slate-300 mt-1">{ind.description}</p>
                        <div className="mt-2 flex flex-wrap gap-2 text-[11px]">
                          <span className="bg-slate-700 text-slate-200 px-1.5 py-0.5 rounded-sm">From <span className="font-mono">{c.column_name}</span></span>
                          <span className="bg-slate-700 text-slate-200 px-1.5 py-0.5 rounded-sm">{ind.aggregation_type}</span>
                          {ind.unit && <span className="bg-slate-700 text-slate-200 px-1.5 py-0.5 rounded-sm">{ind.unit}</span>}
                          <span className="bg-slate-700 text-slate-200 px-1.5 py-0.5 rounded-sm" title={`Score ${c.score} · completeness ${c.completeness}`}>
                            {Math.round(c.score * 100)}% match
                          </span>
                        </div>
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </section>

        {candidates.length > 0 && (
          <div className="flex items-center justify-between pt-2 border-t border-slate-700">
            <p className="text-[11px] text-slate-400">
              Accepting will create new indicators in this program and pre-fill the column mapping.
            </p>
            <Button
              onClick={onAccept}
              disabled={accepting}
              className="bg-amber-400 hover:bg-amber-300 text-slate-900"
              data-testid="accept-candidates-btn"
            >
              <ShieldCheck className="w-3.5 h-3.5 mr-1" /> {accepting ? "Accepting…" : "Accept selected"}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

function FieldSelect({ label, value, onChange, options, testid, allowEmpty = false, emptyLabel = "— none —" }) {
  // Radix Select can't render a SelectItem with an empty value, so we
  // shim a `__none__` sentinel when `allowEmpty` is set. The parent
  // never sees the sentinel — we coerce back to "" on the way out.
  const handleChange = (v) => onChange(v === "__none__" ? "" : v);
  const displayValue = allowEmpty && (value === "" || value == null) ? "__none__" : value;
  return (
    <div>
      <Label className="label-overline">{label}</Label>
      <Select value={displayValue} onValueChange={handleChange}>
        <SelectTrigger className="mt-2" data-testid={testid}><SelectValue placeholder="Select column" /></SelectTrigger>
        <SelectContent>
          {allowEmpty && <SelectItem value="__none__">{emptyLabel}</SelectItem>}
          {options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );
}
